import errorMessages from './errorMessages.json';

interface ErrorMessage {
  title: string;
  message: string;
  action: string;
  technical?: string;
}

/**
 * Converte erros técnicos em mensagens amigáveis para o usuário
 */
export function getHumanizedError(error: any): ErrorMessage {
  // Erro de rede
  if (!error.response) {
    return errorMessages.errors.NETWORK_ERROR;
  }

  const status = error.response?.status;
  const errorCode = error.response?.data?.code;
  const errorType = error.response?.data?.error;

  // Erros de negócio (personalizados do backend)
  if (errorCode && errorMessages.business[errorCode as keyof typeof errorMessages.business]) {
    return errorMessages.business[errorCode as keyof typeof errorMessages.business];
  }

  // Erros HTTP padrão
  const statusKey = status.toString() as keyof typeof errorMessages.errors;
  if (errorMessages.errors[statusKey]) {
    return errorMessages.errors[statusKey];
  }

  // Erro genérico
  return {
    title: 'Erro inesperado',
    message: 'Algo deu errado ao processar sua solicitação.',
    action: 'Tente novamente. Se o problema persistir, entre em contato com o suporte.',
    technical: errorType || 'Unknown Error',
  };
}

/**
 * Retorna mensagens de validação amigáveis
 */
export function getValidationMessage(field: string, rule: string, params?: any): string {
  const messages = errorMessages.validation;
  let message = messages[rule as keyof typeof messages] || 'Campo inválido';

  // Substituir placeholders
  if (params) {
    Object.keys(params).forEach(key => {
      message = message.replace(`{${key}}`, params[key]);
    });
  }

  return message;
}

/**
 * Retorna mensagens de sucesso
 */
export function getSuccessMessage(action: string): string {
  const messages = errorMessages.success;
  return messages[action as keyof typeof messages] || 'Operação realizada com sucesso!';
}

/**
 * Formata erros de validação do backend (NestJS format)
 */
export function formatValidationErrors(errors: any[]): Record<string, string> {
  const formatted: Record<string, string> = {};

  if (Array.isArray(errors)) {
    errors.forEach((error) => {
      if (typeof error === 'string') {
        // Tentar extrair o campo da mensagem
        const match = error.match(/^(\w+):/);
        if (match) {
          const field = match[1];
          formatted[field] = error.replace(`${field}: `, '');
        } else {
          formatted.general = error;
        }
      } else if (error.property && error.constraints) {
        const field = error.property;
        const constraint = Object.values(error.constraints)[0] as string;
        formatted[field] = constraint;
      }
    });
  }

  return formatted;
}

/**
 * Mostra toast de erro (usar com biblioteca de toast)
 */
export function showErrorToast(error: any, toastFunction?: (message: string) => void) {
  const humanError = getHumanizedError(error);
  const message = `${humanError.title}: ${humanError.message}`;
  
  if (toastFunction) {
    toastFunction(message);
  } else {
    // Fallback para alert (não recomendado em produção)
    alert(message);
  }
}

/**
 * Mostra toast de sucesso
 */
export function showSuccessToast(action: string, toastFunction?: (message: string) => void) {
  const message = getSuccessMessage(action);
  
  if (toastFunction) {
    toastFunction(message);
  } else {
    alert(message);
  }
}

/**
 * Verifica se o erro é de autenticação
 */
export function isAuthError(error: any): boolean {
  return error.response?.status === 401;
}

/**
 * Verifica se o erro é de validação
 */
export function isValidationError(error: any): boolean {
  return error.response?.status === 422 || error.response?.status === 400;
}

/**
 * Verifica se é erro de rede
 */
export function isNetworkError(error: any): boolean {
  return !error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error';
}

/**
 * Exemplo de uso com axios interceptor
 */
export function setupErrorInterceptor(axiosInstance: any) {
  axiosInstance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      const humanError = getHumanizedError(error);
      
      // Log para debug
      console.error('API Error:', {
        status: error.response?.status,
        message: humanError.message,
        technical: humanError.technical,
        url: error.config?.url,
      });

      // Adicionar mensagem humanizada ao erro
      error.humanMessage = humanError;

      return Promise.reject(error);
    }
  );
}

export default {
  getHumanizedError,
  getValidationMessage,
  getSuccessMessage,
  formatValidationErrors,
  showErrorToast,
  showSuccessToast,
  isAuthError,
  isValidationError,
  isNetworkError,
  setupErrorInterceptor,
};
